import type { DefaultValues } from "react-hook-form";
import {
  MeterEnergyMeasurementType,
  MeterFrequency,
  MeterMeteringDirection,
  MeterSubMeteringSystem,
  YesNoChoice
} from "../../../types/api.types";
import { MEDIUM_CHOICES } from "../../../utils/constants";
import type { FormInputData } from "../../BuildingBlocks/Forms/Controllers/FormFieldController";
import type { MeterFormData } from "./MeterForm.types";

const MEASUREMENT_TYPE_CHOICES = [
  {
    label: "als Lastgang",
    value: MeterEnergyMeasurementType.Single
  },
  {
    label: "als Zählerstandsgang (beliebige Ablesezeitpunkte)",
    value: MeterEnergyMeasurementType.ArbitraryCumulative
  }
];

const FREQUENCY_CHOICES = [
  { label: "10 Minuten", value: MeterFrequency.TenMinutes },
  { label: "Viertelstunde", value: MeterFrequency.QuarterHour },
  { label: "Stunde", value: MeterFrequency.Hour },
  { label: "Tag", value: MeterFrequency.Day },
  { label: "Monat", value: MeterFrequency.Month },
  { label: "Jahr", value: MeterFrequency.Year }
];

const METERING_DIRECTION_CHOICES = [
  {
    label: "Erzeugung/Einspeisung",
    value: MeterMeteringDirection.GenerationFeedin
  },
  {
    label: "Verbrauch/Entnahme",
    value: MeterMeteringDirection.ConsumptionFeedout
  },
  {
    label: "Zweirichtungszähler",
    value: MeterMeteringDirection.BidirectionalMeter
  },
  {
    label: "Vier-Quadranten-Zähler",
    value: MeterMeteringDirection.FourQuadrantMeter
  }
];

const SUB_METERING_SYSTEM_CHOICES = [
  { label: "Manuelle Eingabe", value: MeterSubMeteringSystem.ManualInput },
  { label: "Machine Account", value: MeterSubMeteringSystem.MachineAccount },
  {
    label: "Breeze (GreenByte)",
    value: MeterSubMeteringSystem.BreezeGreenbyte
  },
  {
    label: "econ 3 (Econ Solutions)",
    value: MeterSubMeteringSystem.Econ3EconSolutions
  },
  {
    label: "EnerBoard (Tengelmann Energie)",
    value: MeterSubMeteringSystem.EnerboardTengelmann
  },
  {
    label: "Energy Platform (Bosch)",
    value: MeterSubMeteringSystem.EnergyPlatformBosch
  },
  { label: "é.VISOR LIMÓN", value: MeterSubMeteringSystem.EvisorLimon },
  { label: "e3m (Emation)", value: MeterSubMeteringSystem.E3mEmation },
  { label: "GridVis (Janitza)", value: MeterSubMeteringSystem.GridvisJanitza },
  {
    label: "Navigator (Siemens)",
    value: MeterSubMeteringSystem.NavigatorSiemens
  },
  { label: "Optimax (ABB)", value: MeterSubMeteringSystem.OptimaxAbb },
  {
    label: "PowerSystem (Enertrag)",
    value: MeterSubMeteringSystem.PowerSystemEnertrag
  },
  { label: "RePLANT (ITS)", value: MeterSubMeteringSystem.ReplantIts },
  {
    label: "Rotorsoft (Drehpunkt)",
    value: MeterSubMeteringSystem.RotorsoftDrehpunkt
  },
  {
    label: "SCADA Solutions (Siemens Gamesa)",
    value: MeterSubMeteringSystem.SiemensGamesaScadaSolutions
  },
  { label: "SIP (Enercon)", value: MeterSubMeteringSystem.SipEnercon },
  { label: "Sonstiges", value: MeterSubMeteringSystem.Misc },
  {
    label: "Vestas Online Business (Vestas)",
    value: MeterSubMeteringSystem.VobVestas
  },
  {
    label: "Windenergie Online (Byte Mee)",
    value: MeterSubMeteringSystem.WeoBytemee
  },
  { label: "WIS (softenergy)", value: MeterSubMeteringSystem.WisSoftenergy },
  {
    label: "Wonder (Windguard)",
    value: MeterSubMeteringSystem.WonderWindguard
  },
  { label: "Qivalo", value: MeterSubMeteringSystem.Qivalo },
  { label: "Wattline", value: MeterSubMeteringSystem.Wattline },
  { label: "Metiundo", value: MeterSubMeteringSystem.Metiundo }
];

const IS_CALIBRATED_CHOICES = [
  { label: "Ja", value: YesNoChoice.yes },
  { label: "Nein", value: YesNoChoice.no }
];

export const METERING_MSB_HELP_TEXT =
  "Eine Mess- oder Marktlokation ist notwendig, um die Lastgänge beim zuständigen Messstellenbetreiber anfragen zu können. Stromzähler, welche für offizielle Abrechnungsprozesse genutzt werden, werden zumeist von einem Messstellenbetreiber (MSB) betrieben. Diese Stromzähler erhalten dann eine Mess- und/oder Marktlokation, damit sie in der Marktkommunikation (Versand von Messwerten bspw. an den Stromlieferanten) eindeutig identifizierbar sind. ";
export const METERING_COMPANY_HELP_TEXT =
  "Insofern Sie den Zähler selbst betreiben, ist Ihren Zähler im Regelfall keine Mess- oder Marktlokation zugewiesen. Diese Zähler können über die Zählernummer (häufig die Seriennummer des Herstellers) eindeutig identifiziert werden.";

export const METER_FORM_FIELD_DATA = {
  global_id: {
    name: "global_id",
    label: "Globale ID",
    type: "text",
    helpText:
      "ID zur standortübergreifenden Identifikation einer physischen Entität"
  },
  name: {
    name: "name",
    label: "Bezeichnung",
    type: "text",
    required: true,
    helpText: "Frei wählbar, wird nur zur Identifikation verwendet"
  },
  medium: {
    name: "medium",
    label: "Medium",
    type: "select",
    required: true,
    data: MEDIUM_CHOICES
  },
  measurement_type: {
    name: "measurement_type",
    label: "Erfassung der Energiemengen",
    type: "select",
    required: true,
    data: MEASUREMENT_TYPE_CHOICES
  },
  frequency: {
    name: "frequency",
    label: "Intervall der Messwerte",
    type: "select",
    required: true,
    data: FREQUENCY_CHOICES
  },
  metering_direction: {
    name: "metering_direction",
    label: "Messrichtung",
    type: "select",
    required: true,
    data: METERING_DIRECTION_CHOICES
  },
  calibration_valid_until: {
    name: "calibration_valid_until",
    label: "Ablaufdatum der Eichgültigkeit (optional)",
    type: "date"
  },
  conversion_factor: {
    name: "conversion_factor",
    label: "Wandlerfaktor",
    type: "number"
  },
  location_feedin: {
    name: "location_feedin",
    label: "Mess- oder Marktlokation Einspeisung",
    type: "select",
    data: [], // populated at runtime from the site's metering-or-market locations
    clearable: true
  },
  location_feedout: {
    name: "location_feedout",
    label: "Mess- oder Marktlokation Entnahme",
    type: "select",
    data: [], // populated at runtime from the site's metering-or-market locations
    clearable: true
  },
  sub_metering_system: {
    name: "sub_metering_system",
    label:
      "Untermess-System (nur erforderlich für interne Zähler/SCADA-Zähler)",
    type: "select",
    data: SUB_METERING_SYSTEM_CHOICES,
    helpText:
      "Werden die Messwerte des Zählers elektronisch erfasst? Wenn die Zählerdaten über ein uns bekanntes Untermess-System verwaltet werden, vereinfacht das den Datenimport. Daten aus Untermesssystemen werden im Regelfall im 7-Tage-Intervall importiert. Ausnahme hiervon ist die Schnittstelle zum Messstellenbetreiber Qivalo. Diese Messwerte werden täglich über die Qivalo-Schnittstelle importiert. Machine Account Daten werden ebenfalls häufiger als wöchentlich importiert."
  },
  is_calibrated: {
    name: "is_calibrated",
    label: "Zähler geeicht (bei Zählern mit MeLo oder MaLo immer 'Ja')*",
    type: "select",
    data: IS_CALIBRATED_CHOICES
  },
  number: {
    name: "number",
    label:
      "Zählernummer bzw. Seriennummer bei SCADA-Zählern (erforderlich falls keine Messlokation vorhanden ist, z.B. interner Zähler)",
    type: "text",
    maxLength: 60,
    helpText:
      "Die Zählernummer wird bei offiziellen „EVU-Zählern“, die über eine Messlokationsnummer verfügen, nicht benötigt. Bei internen Zählern ist die Zählernummer jedoch zwingend zur eindeutigen Identifikation erforderlich.\n\n" +
      "Im Industrie- und Gewerbebereich haben interne Zähler oftmals eine Zählernummer im Energiemanagementsystem oder in der Gebäudeleittechnik hinterlegt. Es gibt hierfür kein bestimmtes Format.\n\n" +
      "In Windparks wird die Seriennummer der WEA dafür verwendet, um die Erzeugungs- und Verbrauchsdaten aus Ihrem SCADA-System eindeutig einer Erzeugungsanlage zuordnen zu können. Die Seriennummer wird immer vom Hersteller der WEA ausgegeben, daher unterscheidet sich das Format zwischen den Herstellern. In der Regel (aber nicht immer) ist dies eine 5- bis 8-stellige Nummer."
  }
} as const satisfies {
  [key in keyof MeterFormData]?: FormInputData<MeterFormData, key>;
};

export const METER_FORM_DEFAULT_VALUES: DefaultValues<MeterFormData> = {
  name: "",
  number: null,
  location_feedin: null,
  location_feedout: null,
  sub_metering_system: null,
  is_calibrated: null,
  calibration_valid_until: null,
  conversion_factor: null
};
